import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { fetchGenerationStatus, cancelGeneration } from '../api/classes';

const GenerationContext = createContext();

const POLL_INTERVAL_MS = 5000;
const STORAGE_KEY = 'teachio-active-generation';

export function GenerationProvider({ children }) {
  const [activeJob, setActiveJob] = useState(null); // { classId, unitId, unitTitle }
  const [jobStatus, setJobStatus] = useState(null); // server response
  const [dismissed, setDismissed] = useState(false);
  const intervalRef = useRef(null);

  // Restore active job from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setActiveJob(parsed);
      } catch { /* ignore */ }
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Polling logic
  useEffect(() => {
    if (!activeJob) return;

    const poll = async () => {
      try {
        const status = await fetchGenerationStatus(activeJob.classId, activeJob.unitId);
        if (!status) {
          // Job no longer exists on server
          stopPolling();
          return;
        }
        setJobStatus(status);

        if (status.status === 'completed' || status.status === 'cancelled' || status.status === 'failed') {
          stopPolling();
        }
      } catch {
        // Network error — keep polling
      }
    };

    poll(); // Immediate first poll
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [activeJob]); // eslint-disable-line react-hooks/exhaustive-deps

  const startGeneration = useCallback(({ classId, unitId, unitTitle }) => {
    const job = { classId, unitId, unitTitle };
    setActiveJob(job);
    setJobStatus(null);
    setDismissed(false);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(job));
  }, []);

  const handleCancel = useCallback(async () => {
    if (!activeJob) return;
    try {
      await cancelGeneration(activeJob.classId, activeJob.unitId);
    } catch (err) {
      console.error('Cancel failed:', err);
    }
  }, [activeJob]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    setActiveJob(null);
    setJobStatus(null);
    stopPolling();
  }, [stopPolling]);

  const value = {
    activeJob,
    jobStatus,
    dismissed,
    startGeneration,
    handleCancel,
    dismiss,
    isGenerating: jobStatus?.status === 'running' || jobStatus?.status === 'pending',
  };

  return (
    <GenerationContext.Provider value={value}>
      {children}
    </GenerationContext.Provider>
  );
}

export const useGeneration = () => {
  const context = useContext(GenerationContext);
  if (!context) throw new Error('useGeneration must be used within a GenerationProvider');
  return context;
};
